/**
 * IDB CRUD operations для `contracts` store (Block 13 PR-G Phase 1).
 *
 * Used by Phase 4-7 Block 13 features:
 *   - saveContract() — autosave debounced 500ms во время редактирования формы 2.13.2
 *   - getContract() — на mount экрана редактирования (URL: /contracts/:id/edit)
 *   - listContractsByUser() — список «Мои договоры» (Экран 2.13.1)
 *   - deleteContract() — Phase 5 удаление из списка
 *   - updateContractSyncStatus() — Phase 9+ Background Sync (currently always 'local')
 *
 * **Layer agnosticism (mirror quotes.ts pattern):** этот модуль работает с
 * `ContractRecord` где `data: ContractDraft` typed на lib layer (через import из
 * `@pomoshnik/shared`). Feature-level wrapper `features/contract/state/savedContractsIdb.ts`
 * provides domain API `SavedContract` (Phase 1).
 *
 * **Transient PII (P-009):** `ContractRecord.data.customerRequisites[].value` хранит
 * sensitive data. Phase 7 реализует `clearTransientRequisites(contractId)` через
 * этот модуль (update existing record с обнулёнными values).
 *
 * Indexes:
 *   - by-userId (для list contracts пользователя на Экране 2.13.1)
 *   - by-syncStatus (для future sync queue scan)
 *   - by-createdAt (для сортировки списка договоров новые-сверху)
 *
 * References:
 *   - ADR-0025 §Q3 (separate contracts store rationale)
 *   - Reference pattern: `quotes.ts` (Block 12 PR-A Phase A3)
 */

import { openDb, type ContractRecord, type SyncStatus } from './database';

export async function saveContract(record: ContractRecord): Promise<void> {
  const db = await openDb();
  await db.put('contracts', record);
}

export async function getContract(id: string): Promise<ContractRecord | undefined> {
  const db = await openDb();
  return db.get('contracts', id);
}

export async function deleteContract(id: string): Promise<void> {
  const db = await openDb();
  await db.delete('contracts', id);
}

export async function listContractsByUser(userId: string): Promise<ContractRecord[]> {
  const db = await openDb();
  return db.getAllFromIndex('contracts', 'by-userId', userId);
}

export async function listContractsBySyncStatus(syncStatus: SyncStatus): Promise<ContractRecord[]> {
  const db = await openDb();
  return db.getAllFromIndex('contracts', 'by-syncStatus', syncStatus);
}

export async function listAllContracts(): Promise<ContractRecord[]> {
  const db = await openDb();
  return db.getAll('contracts');
}

/** Update only syncStatus field (atomic transition local → pending → synced). */
export async function updateContractSyncStatus(id: string, syncStatus: SyncStatus): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('contracts', 'readwrite');
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    throw new Error(`CONTRACT_NOT_FOUND: id=${id}`);
  }
  await tx.store.put({ ...existing, syncStatus });
  await tx.done;
}

/** Bulk count для UI badge (e.g., pending contracts to sync). */
export async function countContractsBySyncStatus(syncStatus: SyncStatus): Promise<number> {
  const db = await openDb();
  return db.countFromIndex('contracts', 'by-syncStatus', syncStatus);
}
